import React, { PureComponent } from 'react';
import bytes from 'bytes';
import { scroller } from 'react-scroll/modules';

import {
  BadgeAd,
  BannerAd,
  Button,
  ImageCompressor,
  ImageResizer,
  ResponsiveTable,
  ScrollWindow,
  ImageFormatChanger
} from 'components';
import { simplifyUrl } from 'utils';
import withPageTracking from 'hoc/withPageTracking';

import CloseIcon from 'assets/icons/close.svg';

import styles from './styles';

class BulkImageAdjust extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      fileSize: '',
      fileSizeSuffix: 'kb',
      quality: '',
      format: '',
      resizeMultiplier: null,
      resizeWidth: null,
      resizeHeight: null
    };
  }

  handleCompressOptionChange = (options) => this.setState({
    ...options
  });

  handleResizeOptionChange = ({ width, widthMultiplier, height, heightMultiplier, multiplier }) => this.setState({
    resizeMultiplier: multiplier,
    resizeWidth: width,
    resizeWidthMultiplier: widthMultiplier,
    resizeHeight: height,
    resizeHeightMultiplier: heightMultiplier
  });

  getNewImageDimensions(image) {
    const {
      resizeWidth,
      resizeWidthMultiplier,
      resizeHeight,
      resizeHeightMultiplier,
      resizeMultiplier
    } = this.state;

    // If at least one side was multiplied by a percentage, take some extra steps
    if (resizeWidthMultiplier || resizeHeightMultiplier) {
      let width, height;

      if (resizeWidthMultiplier) {
        width = image.dimensions.width * resizeWidthMultiplier;
      } else if (resizeWidth) {
        width = resizeWidth;
      }

      if (resizeHeightMultiplier) {
        height = image.dimensions.height * resizeHeightMultiplier;
      } else if (resizeHeight) {
        height = resizeHeight;
      }

      return {
        width: Math.round(width ? width : (height / image.dimensions.height) * image.dimensions.width),
        height: Math.round(height ? height : (width / image.dimensions.width) * image.dimensions.height)
      };
    }

    // Multiply the image dimsions by the specified resize percentage
    if (resizeMultiplier) {
      return {
        width: Math.round(image.dimensions.width * resizeMultiplier),
        height: Math.round(image.dimensions.height * resizeMultiplier)
      };
    }

    // The user wants to skew their output image, oh well
    if (resizeWidth && resizeHeight) {
      return {
        width: resizeWidth,
        height: resizeHeight
      };
    }

    // If only a width is provided, calculate the output height
    if (resizeWidth) {
      return {
        width: resizeWidth,
        height: Math.round(image.dimensions.height / image.dimensions.width * resizeWidth)
      };
    }

    // If only a height is provided, calculate the output width
    if (resizeHeight) {
      return {
        width: Math.round(image.dimensions.width / image.dimensions.height * resizeHeight),
        height: resizeHeight
      };
    }

    // Return the original dimensions if the user hasn't requested a resize
    return {
      width: image.dimensions.width,
      height: image.dimensions.height
    };
  }

  resizeImages = () => {
    const {bulkImages, resizeImages} = this.props;
    const {
      fileSize,
      fileSizeSuffix,
      quality,
      format
    } = this.state;

    resizeImages(bulkImages.map((image) => {
      const body = {
        imageId: image.imageId,
        resize: this.getNewImageDimensions(image)
      };

      if (format) {
        body.imageType = format;
      }

      if (quality || fileSize) {
        body.compress = {};

        if (quality) {
          body.compress.quality = parseInt(quality, 10);
        }

        if (fileSize) {
          body.compress.maxFileSize = Math.round(bytes(`${parseInt(fileSize, 10)}${fileSizeSuffix}`) / 1024); // The API wants kilobytes
        }
      }

      return body;
    }));
  };

  scrollToResizeSettings = () => {
    scroller.scrollTo('resizeSettings', {
      duration: 500,
      smooth: true
    });
  };

  displayOutputImageDimensions(image) {
    const {width, height} = this.getNewImageDimensions(image);

    return `${width} x ${height}`;
  }

  getAction({ imageId }) {
    const {removeImage} = this.props;

    return (
      <Button
        onClick={removeImage}
        clickProps={[imageId]}
      >
        <CloseIcon height="20" width="20" />
      </Button>
    );
  }

  renderList() {
    const {bulkImages} = this.props;
    const columns = {
      fileName: {
        filter: simplifyUrl,
        grow: true,
        heading: 'File Name'
      },
      size: {
        heading: 'Size',
        width: '120px'
      },
      originalDimensions: {
        heading: 'Original (px)',
        width: '130px'
      },
      newDimensions: {
        bold: true,
        heading: 'New (px)',
        width: '130px'
      },
      action: {
        heading: 'Action',
        width: '80px'
      }
    };
    const rows = bulkImages.map((image) => ({
      fileName: image.name,
      size: bytes(image.contentLength),
      originalDimensions: `${image.dimensions.width} x ${image.dimensions.height}`,
      newDimensions: this.displayOutputImageDimensions(image),
      action: this.getAction(image)
    }));

    return (
      <ScrollWindow
        label="Resize Settings"
        onClick={ this.scrollToResizeSettings }
      >
        <ResponsiveTable
          columns={ columns }
          rows={ rows }
          type="default"
        />
      </ScrollWindow>
    );
  }

  render() {
    const {fileSize, fileSizeSuffix, quality, format} = this.state;

    return (
      <div className='sideBySide container'>
        <div className='maxWidthSection'>
          <div className={`section ${styles.noTopPadding}`}>
            {this.renderList()}
          </div>
          <div
            className='section'
            ref={ (elem) => {
              scroller.register('resizeSettings', elem);
            } }
          >
            <h2><span className='step'>1</span> Resize your Images</h2>
            <ImageResizer onOptionChange={this.handleResizeOptionChange} />
          </div>
          <div className='section'>
            <h2><span className='step'>2</span> Optimize Your Image (optional)</h2>
            <ImageCompressor
              showFileSizeInput="yes"
              fileSize={fileSize}
              fileSizeSuffix={fileSizeSuffix}
              quality={quality}
              format={format}
              onOptionChange={this.handleCompressOptionChange}
            />
          </div>
          <div className='section'>
            <h2><span className='step'>3</span> Save Image As</h2>
            <ImageFormatChanger
              format={format}
              onOptionChange={this.handleCompressOptionChange}
            />
          </div>

          <BannerAd />
          <div className='section'>
            <Button
              className='main-button'
              onClick={this.resizeImages}
            >
              Resize Images
            </Button>
          </div>
        </div>
        <BadgeAd />
      </div>
    );
  }
}

export default withPageTracking(BulkImageAdjust, '/bulk-editor.html');



// WEBPACK FOOTER //
// ./src/containers/BulkResize/BulkImageAdjust.jsx